import { Button } from './ui/button';

const DOT_PILL = {
  live:     { dot: '#1a9e6a', bg: '#e8f5ee', text: '#1a6b3a' },
  soon:     { dot: '#ba7517', bg: '#fef3e2', text: '#7a4800' },
  full:     { dot: '#0d4a8a', bg: '#e8f0f9', text: '#082f5a' },
  finished: { dot: '#7a93ab', bg: '#f3f6fa', text: '#4a6078' },
};

const AVATAR_COLORS = ['#3B82F6','#22C55E','#F59E0B','#6B7280'];
const RANK_COLORS = ['#854F0B','#5F5E5A','#993C1D'];

function PlayerBubbles({ players = [], count = 0 }) {
  const shown = players.slice(0, 3);
  const overflow = Math.max(0, count - 3);
  return (
    <div className="flex items-center">
      {shown.map((p, i) => (
        <div
          key={i}
          className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[8px] font-semibold text-white border-2 border-white"
          style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length], marginLeft: i > 0 ? -6 : 0, zIndex: 3 - i }}
        >
          {(p.initials || p.name || '?').substring(0, 2).toUpperCase()}
        </div>
      ))}
      {overflow > 0 && (
        <div
          className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[8px] font-medium text-gray-500 bg-gray-100 border-2 border-white"
          style={{ marginLeft: -6 }}
        >
          +{overflow}
        </div>
      )}
    </div>
  );
}

export default function LiveGameCard({
  status = 'live',
  title,
  subtitle,
  room_code,
  player_count = 0,
  max_players = 30,
  question_progress,
  starts_in,
  finished_at,
  top3 = [],
  onJoin,
  onSeeResults,
  isDark = false,
}) {
  const colors = DOT_PILL[status] || DOT_PILL.live;
  const isLive = status === 'live';
  const isSoon = status === 'soon';
  const isFull = status === 'full';
  const isFinished = status === 'finished';
  const textMain = isDark ? 'text-white' : 'text-gray-900';
  const textMuted = isDark ? 'text-gray-400' : 'text-gray-500';
  const borderColor = isDark ? 'border-gray-800' : 'border-gray-200';
  const capacityPct = max_players > 0 ? Math.min((player_count / max_players) * 100, 100) : 0;

  return (
    <div
      className={`rounded-[14px] border p-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'} ${isFull ? 'opacity-[0.85]' : ''}`}
      data-testid={`live-card-${status}`}
    >
      {/* Status row */}
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <span className="relative w-2 h-2 flex-shrink-0">
            {isLive && <span className="absolute inset-0 rounded-full animate-[livePulse_1.5s_ease-in-out_infinite]" style={{ background: colors.dot }} />}
            <span className="absolute inset-0 rounded-full" style={{ background: colors.dot }} />
          </span>
          <span
            className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={{ background: colors.bg, color: colors.text }}
          >
            {isLive && `Live · ${question_progress || 'In progress'}`}
            {isSoon && `Starts in ${starts_in || 'soon'}`}
            {isFull && `Full · ${player_count}/${max_players}`}
            {isFinished && `Finished · ${finished_at || 'recently'}`}
          </span>
        </div>
        <span className={`text-[11px] font-mono ${textMuted}`}>
          {isFinished ? `${player_count} players` : room_code}
        </span>
      </div>

      {/* Title + subtitle */}
      <h4 className={`text-[13px] font-medium ${textMain} mb-0.5`}>{title}</h4>
      <p className={`text-[11px] ${textMuted} mb-3`}>{subtitle}</p>

      {/* Divider */}
      <div className={`h-px ${isDark ? 'bg-gray-800' : 'bg-gray-100'} mb-3`} />

      {/* Bottom — varies by state */}
      {isLive && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlayerBubbles players={top3.length ? top3 : Array.from({ length: Math.min(player_count, 3) }, (_, i) => ({ name: `P${i + 1}` }))} count={player_count} />
            <span className={`text-[11px] ${textMuted}`}>{player_count} playing</span>
          </div>
          <Button size="sm" onClick={onJoin} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-[10px] px-4 text-[12px] font-medium h-7">
            Join now
          </Button>
        </div>
      )}

      {isSoon && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className={`w-full h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="h-full rounded-full bg-brand-500" style={{ width: `${capacityPct}%` }} />
            </div>
            <p className={`text-[11px] ${textMuted} mt-1`}>{player_count} / {max_players} · {max_players - player_count} spots left</p>
          </div>
          <Button size="sm" onClick={onJoin} className="bg-brand-500 hover:bg-brand-600 text-white rounded-[10px] px-4 text-[12px] font-medium h-7">
            Join lobby
          </Button>
        </div>
      )}

      {isFull && (
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1">
            <div className={`w-full h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
              <div className="h-full rounded-full bg-brand-500" style={{ width: '100%' }} />
            </div>
            <p className={`text-[11px] ${textMuted} mt-1`}>Starting any moment</p>
          </div>
          <Button size="sm" disabled className="rounded-[10px] px-4 text-[12px] font-medium h-7 opacity-50 cursor-not-allowed">
            Full
          </Button>
        </div>
      )}

      {isFinished && (
        <>
          {top3.slice(0, 3).map((p, i) => (
            <div key={i} className={`flex items-center gap-2.5 py-1.5 ${i < 2 ? `border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}` : ''}`}>
              <span className="w-4 text-[11px] font-semibold text-center" style={{ color: RANK_COLORS[i] || '#9ca3af' }}>{i + 1}</span>
              <div className="w-[22px] h-[22px] rounded-full flex items-center justify-center text-[8px] font-semibold text-white" style={{ background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                {(p.initials || p.name || '?').substring(0, 2).toUpperCase()}
              </div>
              <span className={`text-[12px] flex-1 ${textMain}`}>{p.name}</span>
              <span className={`text-[12px] font-medium ${textMuted}`}>{p.score} pts</span>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={onSeeResults}
            className={`w-full mt-3 rounded-[10px] text-[12px] font-medium h-8 ${isDark ? 'bg-brand-900/30 text-brand-400' : 'bg-brand-50 text-brand-600'}`}
          >
            See full results
          </Button>
        </>
      )}
    </div>
  );
}
